import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

export type AppearanceTheme = 'light' | 'dark';

type UserPreferences = {
  appearance?: {
    theme?: AppearanceTheme;
  };
  [key: string]: unknown;
};

function isAppearanceTheme(value: unknown): value is AppearanceTheme {
  return value === 'light' || value === 'dark';
}

export async function getUserPreferences(userId: string): Promise<UserPreferences | null> {
  const { data, error } = await supabase
    .from('user_preferences')
    .select('preferences')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return (data?.preferences as UserPreferences | null) ?? null;
}

export async function getUserThemePreference(userId: string): Promise<AppearanceTheme | null> {
  const preferences = await getUserPreferences(userId);
  const candidate = preferences?.appearance?.theme;
  return isAppearanceTheme(candidate) ? candidate : null;
}

export async function saveUserThemePreference(userId: string, theme: AppearanceTheme): Promise<void> {
  const currentPreferences = (await getUserPreferences(userId)) ?? {};
  const nextPreferences: UserPreferences = {
    ...currentPreferences,
    appearance: {
      ...(currentPreferences.appearance ?? {}),
      theme,
    },
  };

  const { error } = await supabase
    .from('user_preferences')
    .upsert(
      {
        user_id: userId,
        preferences: nextPreferences as unknown as Json,
      },
      { onConflict: 'user_id' }
    );

  if (error) {
    throw error;
  }
}
